import type { SliceMetrics, StreamSliceNode } from '@/analysis/searchContract'
import type { ActiveFilters } from '@/types'
import type { CatalogStreamItem } from '@/store/catalogStore'
import { toNoDataStreamShell } from '@/hooks/retainRecentStreamNodes'

function listHit(list: string[], value: string | undefined | null): boolean {
  if (!list.length) return true
  if (value == null || value === '') return false
  const v = String(value)
  return list.some((x) => x === v || v.includes(x) || x.includes(v))
}

/** Catalog membership filters for Include inactive (studio/game/brand + stream filters). */
export function catalogStreamMatchesFilters(
  c: CatalogStreamItem,
  filters: ActiveFilters,
): boolean {
  if (c.deleted_at) return false
  if (filters.studio.length && !listHit(filters.studio, c.studio_code)) return false
  if (filters.game.length && !listHit(filters.game, c.game_code)) return false
  if (filters.brand.length) {
    const brands = c.brand_ids ?? []
    if (!brands.some((b) => listHit(filters.brand, b))) return false
  }
  if (filters.streamKey.length && !listHit(filters.streamKey, c.stream_id)) return false
  if (filters.sourceUrl.length) {
    const ok = filters.sourceUrl.some(
      (s) =>
        listHit([s], c.stream_id) ||
        listHit([s], c.stream_url) ||
        listHit([s], c.url_masked),
    )
    if (!ok) return false
  }
  if (filters.streamType.length && !listHit(filters.streamType, c.type)) return false
  return true
}

function emptyMetrics(): SliceMetrics {
  return {} as SliceMetrics
}

export function catalogToNoDataShell(c: CatalogStreamItem, nowMs: number): StreamSliceNode {
  const sid = String(c.stream_id ?? '').trim()
  const status =
    c.status === 'online' || c.status === 'idle' || c.status === 'recently-offline'
      ? c.status
      : null
  return toNoDataStreamShell({
    kind: 'stream',
    type: 'stream',
    streamId: sid,
    label: sid,
    value: sid,
    windowStartMs: nowMs,
    windowEndMs: nowMs,
    tickCount: 0,
    usersTotal: 0,
    totalStreams: 0,
    totalObservationMs: 0,
    excludedCount: 0,
    avgSeverity: null,
    severity: 'green',
    worstMetric: null,
    worstMetricSeverity: 2,
    metrics: emptyMetrics(),
    studio: c.studio_code ?? null,
    game: c.game_code ?? null,
    streamType: c.type ?? null,
    catalogUrl: c.stream_url ?? c.url_masked ?? null,
    status,
    lastSeenMs: c.lastSeenMs ?? null,
  })
}

/**
 * Include inactive membership: all catalog streams matching filters, with live
 * snapshot metrics overlaid when present. Soft-retain is not used in this mode.
 */
export function mergeCatalogStreamMembership(
  liveStreams: StreamSliceNode[],
  catalog: CatalogStreamItem[],
  filters: ActiveFilters,
  nowMs: number,
): StreamSliceNode[] {
  const byId = new Map<string, StreamSliceNode>()
  for (const n of liveStreams) {
    const id = String(n.streamId ?? n.value ?? '').trim()
    if (!id) continue
    byId.set(id, n)
  }

  const out: StreamSliceNode[] = []
  const seen = new Set<string>()

  for (const c of catalog) {
    if (!catalogStreamMatchesFilters(c, filters)) continue
    const id = String(c.stream_id ?? '').trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(byId.get(id) ?? catalogToNoDataShell(c, nowMs))
  }

  // Keep uncatalogued live streams that the API already returned.
  for (const n of liveStreams) {
    const id = String(n.streamId ?? n.value ?? '').trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(n)
  }

  return out
}
